// frontend/src/pages/AudioRecorder/utils/transcriptionBackupUtils.js
/**
 * Transcription Backup Utilities
 * Handles periodic backup of live transcription data to backend storage.
 * Implements 20-second interval saves with automatic cleanup.
 */

import { getBackendURL, supabaseUrl } from "../../../components/env";
import { getSupabase } from "../../../components/supabaseClient";
import {
  getUserSchema,
  resolveStorageFolderYearDateForPatientId,
  sanitizePatientNameForBackup,
} from "./databaseUtils";

// Backup configuration
const BACKUP_CONFIG = {
  INTERVAL_MS: 20000, // 20 seconds - configurable backup interval
  MAX_BACKUPS_PER_SESSION: 100,
  RETENTION_HOURS: 24,
  BUCKET_NAME: "transcription-backups",
  ENABLE_BACKUP: true,
  USE_JSON_FORMAT: true, // Use JSON for better data integrity
};

// State management
let backupIntervalId = null;
let backupCount = 0;
let lastBackupTime = null;
let currentSessionId = null;
let currentPathInfo = null;

/**
 * Every backup for a session overwrites ONE storage object
 * ({name}-{ehr}_Audio{n}.json), so two in-flight saves can land out of order and
 * leave the file holding an older, shorter transcript. All saves therefore go
 * through a single promise chain (sent strictly one after another) and each
 * snapshot carries the time it was captured; a snapshot older than the last
 * one already written for the same session is dropped instead of regressing
 * the file.
 */
let saveChain = Promise.resolve();
let lastSavedSessionId = null;
let lastSavedCapturedAt = 0;
let lastSavedText = null;

/** Folder year/date lookups hit the patient row; memoize per session. */
let folderCache = new Map();
let bucketEnsured = null;

const toCapturedAtMs = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Initialize a backup session for a recording
 * @param {string} sessionId - Unique session identifier
 * @param {string} patientId - Patient identifier
 * @param {Object} pathInfo - Path information for folder structure
 * @param {string} pathInfo.schemaName - Schema name for folder structure
 * @param {string} pathInfo.year - Year for folder structure
 * @param {string} pathInfo.date - Date for folder structure
 * @param {string} pathInfo.patientName - Patient name for filename
 * @param {string} pathInfo.ehrId - EHR ID for filename
 * @param {number} pathInfo.audioNumber - Audio number for filename
 */
export const initBackupSession = (sessionId, patientId, pathInfo = {}) => {
  console.log("💾 [TRANSCRIPTION_BACKUP] Initializing backup session:", {
    sessionId,
    patientId,
    pathInfo,
    interval: BACKUP_CONFIG.INTERVAL_MS,
  });

  currentSessionId = sessionId;
  backupCount = 0;
  lastBackupTime = null;
  currentPathInfo = pathInfo;
  folderCache = new Map();

  // Ensure backup bucket exists on the server
  ensureBackupBucket();
};

/**
 * Idempotent variant used by every popup message handler (periodic tick, pause,
 * abrupt close, stop). Re-running initBackupSession on each of those reset the
 * backup counters and replaced the carefully resolved pathInfo with whatever the
 * message happened to carry; here an already-active session keeps its state and
 * only fills in path fields it does not have yet.
 */
export const ensureBackupSession = (sessionId, patientId, pathInfo = {}) => {
  if (!sessionId) return false;
  if (currentSessionId !== sessionId) {
    initBackupSession(sessionId, patientId, pathInfo);
    return true;
  }
  const merged = { ...(currentPathInfo || {}) };
  let changed = false;
  for (const [key, value] of Object.entries(pathInfo || {})) {
    const existing = merged[key];
    const missing =
      existing == null ||
      existing === "" ||
      existing === "unknown_ehr" ||
      existing === "public";
    if (missing && value != null && value !== "" && value !== existing) {
      merged[key] = value;
      changed = true;
    }
  }
  if (changed) currentPathInfo = merged;
  return false;
};

/**
 * Replace the path info of the active session (no-op when the session has
 * already been superseded). Lets RECORDING_STARTED register the session
 * synchronously and refine the folder/EHR from the DB afterwards.
 */
export const updateBackupSessionPathInfo = (sessionId, pathInfo) => {
  if (!sessionId || currentSessionId !== sessionId || !pathInfo) return false;
  currentPathInfo = { ...(currentPathInfo || {}), ...pathInfo };
  return true;
};

/**
 * Start periodic transcription backup
 * @param {Function} getTranscriptionData - Function that returns current transcription state
 * @param {string} patientId - Patient identifier
 */
export const startPeriodicBackup = (getTranscriptionData, patientId) => {
  if (!BACKUP_CONFIG.ENABLE_BACKUP) {
    console.log("💾 [TRANSCRIPTION_BACKUP] Backup is disabled");
    return null;
  }

  if (backupIntervalId) {
    console.log(
      "💾 [TRANSCRIPTION_BACKUP] Backup already running, stopping previous",
    );
    stopPeriodicBackup();
  }

  console.log(
    "💾 [TRANSCRIPTION_BACKUP] Starting periodic backup with",
    BACKUP_CONFIG.INTERVAL_MS,
    "ms interval",
  );

  // Initial backup
  triggerBackup(getTranscriptionData, patientId);

  // Set up interval for periodic backups
  backupIntervalId = setInterval(() => {
    triggerBackup(getTranscriptionData, patientId);
  }, BACKUP_CONFIG.INTERVAL_MS);

  return backupIntervalId;
};

/**
 * Stop periodic backup
 */
export const stopPeriodicBackup = () => {
  if (backupIntervalId) {
    clearInterval(backupIntervalId);
    backupIntervalId = null;
    console.log("💾 [TRANSCRIPTION_BACKUP] Periodic backup stopped");
  }
};

/**
 * Trigger an immediate backup
 * @param {Function} getTranscriptionData - Function that returns current transcription state
 * @param {string} patientId - Patient identifier
 */
export const triggerBackup = async (getTranscriptionData, patientId) => {
  if (!currentSessionId) {
    console.warn("💾 [TRANSCRIPTION_BACKUP] No active session for backup");
    return { success: false, error: "No active session" };
  }

  try {
    const transcriptionData =
      typeof getTranscriptionData === "function"
        ? getTranscriptionData()
        : getTranscriptionData;

    const transcriptRaw = transcriptionData?.transcriptText;
    if (
      !transcriptionData ||
      String(transcriptRaw ?? "").trim().length === 0
    ) {
      console.log("💾 [TRANSCRIPTION_BACKUP] No transcription data to backup");
      return { success: false, error: "No transcription data" };
    }

    // A snapshot from a previous session (ref not yet refreshed after
    // Save & continue) must not be written under the new session's file.
    if (
      transcriptionData.sessionId &&
      transcriptionData.sessionId !== currentSessionId
    ) {
      return { success: false, error: "Snapshot belongs to another session" };
    }

    // Get path info for folder structure (from currentPathInfo)
    const pathInfo = currentPathInfo || {};

    const backupPayload = {
      patient_id: patientId || "unknown",
      session_id: currentSessionId, // Same session_id as audio for matching
      transcript_text: transcriptionData.transcriptText,
      duration: transcriptionData.duration || 0,
      timestamp: new Date().toISOString(),
      capturedAt: transcriptionData.capturedAt || Date.now(),
      // New path parameters for folder structure matching audio files
      schema_name: pathInfo.schemaName || "public",
      year: pathInfo.year || new Date().getFullYear().toString(),
      date: pathInfo.date || new Date().toISOString().split("T")[0],
      patient_name: pathInfo.patientName || "",
      ehr_id: pathInfo.ehrId || "unknown_ehr",
      audio_number: pathInfo.audioNumber || 1,
      metadata: {
        backup_number: backupCount + 1,
        last_backup_time: lastBackupTime,
        recording_state: transcriptionData.recordingState || "unknown",
        source: "periodic_auto_backup",
        session_id: currentSessionId, // Must match parent audio's session_id
        audio_number: pathInfo.audioNumber || 1,
        audio_index: pathInfo.audioNumber || 1, // For metadata-driven matching
        bucket: BACKUP_CONFIG.BUCKET_NAME,
      },
    };

    const result = await saveTranscriptionBackup(backupPayload);

    if (result.success && !result.skipped) {
      backupCount++;
      lastBackupTime = new Date().toISOString();
      console.log(
        "💾 [TRANSCRIPTION_BACKUP] Backup #" +
          backupCount +
          " saved successfully",
      );
    }

    return result;
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Error triggering backup:", error);
    return { success: false, error: error.message };
  }
};

const resolveFolderYearDateCached = async (patientId, dateHint) => {
  const key = `${patientId}|${dateHint || ""}`;
  if (folderCache.has(key)) return folderCache.get(key);
  const resolved = await resolveStorageFolderYearDateForPatientId(
    patientId,
    dateHint,
  );
  folderCache.set(key, resolved);
  return resolved;
};

const performTranscriptionBackupSave = async (data, pathInfo) => {
  try {
    const backendUrl = getBackendURL();
    const endpoint = BACKUP_CONFIG.USE_JSON_FORMAT
      ? "/api/transcription-backup/save/"
      : "/api/transcription-backup/save-txt/";

    const sessionId = data.sessionId || data.session_id || null;
    const transcriptText = data.transcriptText || data.transcript_text || "";
    const capturedAt = toCapturedAtMs(data.capturedAt || data.timestamp);

    // Ordering guard: a snapshot taken before the one already on disk for this
    // session would roll the file back — drop it. Identical text is a no-op.
    if (sessionId && sessionId === lastSavedSessionId) {
      if (
        capturedAt &&
        lastSavedCapturedAt &&
        capturedAt < lastSavedCapturedAt
      ) {
        console.log(
          "💾 [TRANSCRIPTION_BACKUP] Skipping stale snapshot (older than last saved)",
          { capturedAt, lastSavedCapturedAt },
        );
        return { success: true, skipped: true, reason: "stale" };
      }
      if (lastSavedText !== null && transcriptText === lastSavedText) {
        return { success: true, skipped: true, reason: "unchanged" };
      }
    }

    const patientIdForPath = data.patientId || data.patient_id || "unknown";
    const callerDateHint = data.date || pathInfo.date || null;
    const { year: resolvedYear, date: resolvedDate } =
      await resolveFolderYearDateCached(patientIdForPath, callerDateHint);

    // Transform camelCase to snake_case for backend compatibility
    const rawPatientStem =
      data.patient_name || pathInfo.patientName || "";

    const transformedData = {
      patient_id: patientIdForPath,
      session_id: sessionId,
      transcript_text: transcriptText,
      duration: data.duration || 0,
      timestamp: data.timestamp || new Date().toISOString(),
      metadata: data.metadata || {},
      // Folder path: prefer DB patient visit date over pathInfo (matches backup-audio)
      schema_name: data.schema_name || pathInfo.schemaName || "public",
      year: resolvedYear,
      date: resolvedDate,
      patient_name: sanitizePatientNameForBackup(rawPatientStem),
      ehr_id: data.ehr_id || pathInfo.ehrId || "unknown_ehr",
      audio_number: data.audio_number || pathInfo.audioNumber || 1,
    };

    console.log(
      "💾 [TRANSCRIPTION_BACKUP] Saving backup to:",
      backendUrl + endpoint,
      { transformedData },
    );

    const response = await fetch(backendUrl + endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(transformedData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to save backup");
    }

    if (sessionId) {
      lastSavedSessionId = sessionId;
      lastSavedCapturedAt = capturedAt || Date.now();
      lastSavedText = transcriptText;
    }

    console.log("💾 [TRANSCRIPTION_BACKUP] Backup saved:", {
      url: result.file_url,
      path: result.file_path,
      filename: result.filename,
    });

    return result;
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Save error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Save transcription backup to backend.
 * Saves are queued and sent one at a time (see saveChain) so the in-place
 * overwrite on the server always happens in snapshot order.
 * @param {Object} data - Backup data payload
 */
export const saveTranscriptionBackup = (data) => {
  // Snapshot the session path info now: the session may be ended/replaced by
  // the time this save reaches the front of the queue.
  const pathInfo = data?.pathInfo || currentPathInfo || {};
  const run = saveChain.then(() => performTranscriptionBackupSave(data, pathInfo));
  saveChain = run.catch(() => {});
  return run;
};

/**
 * Ensure backup bucket exists on the server (one request per page load).
 */
export const ensureBackupBucket = async () => {
  if (bucketEnsured) return bucketEnsured;
  bucketEnsured = (async () => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(
        backendUrl + "/api/transcription-backup/ensure-bucket/",
      );
      const result = await response.json();

      if (result.success) {
        console.log(
          "💾 [TRANSCRIPTION_BACKUP] Bucket verified/created:",
          result.bucket_name,
        );
      } else {
        console.warn(
          "💾 [TRANSCRIPTION_BACKUP] Bucket verification failed:",
          result.error,
        );
        bucketEnsured = null; // allow a retry next time
      }

      return result;
    } catch (error) {
      console.error("💾 [TRANSCRIPTION_BACKUP] Bucket check error:", error);
      bucketEnsured = null;
      return { success: false, error: error.message };
    }
  })();
  return bucketEnsured;
};

/**
 * Get the latest backup for a session
 * @param {string} patientId - Patient identifier
 * @param {string} sessionId - Session identifier
 */
export const getLatestBackup = async (patientId, sessionId) => {
  try {
    const backendUrl = getBackendURL();
    const url = `${backendUrl}/api/transcription-backup/latest/?patient_id=${encodeURIComponent(patientId)}&session_id=${encodeURIComponent(sessionId)}`;

    console.log("💾 [TRANSCRIPTION_BACKUP] getLatestBackup calling URL:", url);

    const response = await fetch(url);
    console.log(
      "💾 [TRANSCRIPTION_BACKUP] getLatestBackup response status:",
      response.status,
    );

    // Handle 404 gracefully - it's not an error, just no backup exists
    if (response.status === 404) {
      console.log(
        "💾 [TRANSCRIPTION_BACKUP] No backup found (404 is expected)",
      );
      return null;
    }

    const result = await response.json();

    if (result.success && result.backup) {
      console.log("💾 [TRANSCRIPTION_BACKUP] Retrieved latest backup");
      return result.backup;
    }

    console.log(
      "💾 [TRANSCRIPTION_BACKUP] No backup found in response:",
      result,
    );
    return null;
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Get latest backup error:", error);
    return null;
  }
};

/**
 * List all backups for a session
 * @param {string} patientId - Patient identifier
 * @param {string} sessionId - Session identifier
 */
export const listSessionBackups = async (patientId, sessionId) => {
  try {
    const backendUrl = getBackendURL();
    const url = `${backendUrl}/api/transcription-backup/list/?patient_id=${encodeURIComponent(patientId)}&session_id=${encodeURIComponent(sessionId)}`;

    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      console.log(
        "💾 [TRANSCRIPTION_BACKUP] Found",
        result.count,
        "backups for session",
      );
      return result.backups;
    }

    return [];
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] List backups error:", error);
    return [];
  }
};

/**
 * Get any available transcription backup for a patient (regardless of session)
 * @param {string} patientId - Patient identifier
 */
export const getAnyTranscriptionBackup = async (patientId) => {
  try {
    const backendUrl = getBackendURL();
    // Use a different endpoint that lists all backups for the patient
    const url = `${backendUrl}/api/transcription-backup/list/?patient_id=${encodeURIComponent(patientId)}`;

    console.log(
      "💾 [TRANSCRIPTION_BACKUP] Calling getAnyTranscriptionBackup with patientId:",
      patientId,
    );
    console.log("💾 [TRANSCRIPTION_BACKUP] Full URL:", url);

    const response = await fetch(url);
    console.log("💾 [TRANSCRIPTION_BACKUP] Response status:", response.status);
    const result = await response.json();
    console.log("💾 [TRANSCRIPTION_BACKUP] Full result:", result);

    if (result.success && result.backups && result.backups.length > 0) {
      // Return the most recent backup
      const sortedBackups = result.backups.sort(
        (a, b) =>
          new Date(b.backup_timestamp || b.created_at) -
          new Date(a.backup_timestamp || a.created_at),
      );
      console.log(
        "💾 [TRANSCRIPTION_BACKUP] Found any backup:",
        sortedBackups[0],
      );
      return sortedBackups[0];
    }

    console.log("💾 [TRANSCRIPTION_BACKUP] No backups found for patient");
    return null;
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Get any backup error:", error);
    return null;
  }
};

function patientIdMatchesBackup(patientId, jsonPatientId) {
  return String(jsonPatientId || "") === String(patientId || "");
}

/** Public object URL with per-segment encoding (commas etc. in filenames). */
function buildPublicTranscriptionObjectUrl(pathRelative) {
  const base = supabaseUrl.replace(/\/$/, "");
  const parts = String(pathRelative)
    .split("/")
    .filter(Boolean)
    .map((s) => encodeURIComponent(s));
  return `${base}/storage/v1/object/public/${BACKUP_CONFIG.BUCKET_NAME}/${parts.join("/")}`;
}

/**
 * When Storage list() fails or returns nothing, fetch likely JSON paths directly.
 * Matches backend naming: safe_patient_name.replace(/ /g,'_').replace(/\//g,'_') + '-' + ehr + '_Audio' + n + '.json'
 * @param {string[]} folderPaths - e.g. ["SOC_WORK/2026/2026-04-06"]
 */
async function probeDirectTranscriptionUrlsForFolders(
  patientId,
  mergedState,
  folderPaths,
) {
  if (!patientId || !supabaseUrl || !mergedState || !folderPaths?.length) {
    return null;
  }

  const ehr = String(
    mergedState.EHR_ID || mergedState.ehr_id || mergedState.ehrId || "",
  ).trim();
  const rawName = mergedState.patient_name || mergedState.name || "";
  if (!ehr || !rawName) return null;

  const safeBackend = String(rawName).replace(/ /g, "_").replace(/\//g, "_");
  const moLen = Array.isArray(mergedState.more_audios)
    ? mergedState.more_audios.length
    : 0;
  const preferredAudioNum = moLen + 1;
  const audioNums = new Set();
  audioNums.add(preferredAudioNum);
  for (let n = 1; n <= 12; n++) audioNums.add(n);

  const filenames = [];
  for (const n of [...audioNums].sort((a, b) => a - b)) {
    filenames.push(`${safeBackend}-${ehr}_Audio${n}.json`);
  }

  const candidates = [];
  const seenFolders = new Set(
    folderPaths.map((f) => f.replace(/^\/+|\/+$/g, "")),
  );

  for (const folder of seenFolders) {
    for (const fname of filenames) {
      const rel = `${folder}/${fname}`;
      const publicUrl = buildPublicTranscriptionObjectUrl(rel);
      try {
        const res = await fetch(publicUrl);
        if (!res.ok) continue;
        const data = await res.json();
        if (data && patientIdMatchesBackup(patientId, data.patient_id)) {
          candidates.push({
            transcriptText: data.transcript_text || "",
            duration: data.duration,
            metadata: data.metadata || {},
            backupTimestamp: data.created_at,
          });
        }
      } catch (e) {
        console.warn(
          "[TRANSCRIPTION_BACKUP] Direct URL probe failed:",
          rel,
          e?.message || e,
        );
      }
    }
  }

  if (candidates.length === 0) return null;
  candidates.sort(
    (a, b) =>
      new Date(b.backupTimestamp || 0).getTime() -
      new Date(a.backupTimestamp || 0).getTime(),
  );
  return candidates[0];
}

async function fetchTranscriptionJsonCandidatesFromFolder(
  patientId,
  folder,
  candidates,
) {
  if (!patientId || !supabaseUrl || !folder) return;

  const supabase = await getSupabase();
  const normalized = folder.replace(/^\/+|\/+$/g, "");
  const { data: files, error } = await supabase.storage
    .from(BACKUP_CONFIG.BUCKET_NAME)
    .list(normalized, {
      limit: 100,
      sortBy: { column: "created_at", order: "desc" },
    });
  if (error) {
    console.warn(
      "[TRANSCRIPTION_BACKUP] Storage list failed:",
      normalized,
      error.message,
    );
    return;
  }

  for (const f of files || []) {
    if (!f.name || !f.name.endsWith(".json")) continue;
    if (f.name.startsWith(".") || f.name === ".keep") continue;
    const path = `${normalized}/${f.name}`;
    const publicUrl = buildPublicTranscriptionObjectUrl(path);
    try {
      const res = await fetch(publicUrl);
      if (!res.ok) continue;
      const data = await res.json();
      if (data && patientIdMatchesBackup(patientId, data.patient_id)) {
        candidates.push({
          transcriptText: data.transcript_text || "",
          duration: data.duration,
          metadata: data.metadata || {},
          backupTimestamp: data.created_at || f.created_at,
        });
      }
    } catch (e) {
      console.warn(
        "[TRANSCRIPTION_BACKUP] Failed to read JSON at",
        path,
        e?.message || e,
      );
    }
  }
}

/**
 * List JSON files in one transcription-backups folder (same path shape as audio:
 * schema/year/date) and return the newest matching patient_id.
 *
 * @param {string} storageFolder - e.g. "SOC_WORK/2026/2026-04-06" (no bucket name)
 */
export async function findTranscriptionBackupInStorageFolder(
  patientId,
  storageFolder,
  mergedStateForProbe = null,
) {
  if (!patientId || !supabaseUrl || !storageFolder) return null;
  try {
    const candidates = [];
    await fetchTranscriptionJsonCandidatesFromFolder(
      patientId,
      storageFolder,
      candidates,
    );
    if (candidates.length === 0 && mergedStateForProbe) {
      const probed = await probeDirectTranscriptionUrlsForFolders(
        patientId,
        mergedStateForProbe,
        [storageFolder],
      );
      if (probed) return probed;
    }
    if (candidates.length === 0) return null;
    candidates.sort(
      (a, b) =>
        new Date(b.backupTimestamp || 0).getTime() -
        new Date(a.backupTimestamp || 0).getTime(),
    );
    return candidates[0];
  } catch (e) {
    console.error(
      "[TRANSCRIPTION_BACKUP] findTranscriptionBackupInStorageFolder:",
      e,
    );
    return null;
  }
}

/**
 * List transcription-backups in Supabase Storage and match JSON by patient_id.
 * Use when backend search misses (e.g. filename uses "Team,_Vitamed-..." but search
 * builds "Team_Vitamed-..." from a different name string).
 *
 * @param {string} patientId
 * @param {object} mergedState - merged UI state + DB row (date_, scheduledDate, date)
 * @param {string[]} [extraFolders] - additional schema/year/date paths to scan (e.g. same folder as audio backup)
 * @returns {Promise<{ transcriptText: string, duration: number, metadata: object, backupTimestamp: string }|null>}
 */
export async function findTranscriptionBackupInSupabaseStorage(
  patientId,
  mergedState = {},
  extraFolders = [],
) {
  if (!patientId || !supabaseUrl) return null;

  try {
    const schema =
      (await getUserSchema()) || mergedState.schema_name || "public";

    const today = new Date().toISOString().split("T")[0];
    const dateCandidates = [
      mergedState.date_,
      mergedState.scheduledDate,
      mergedState.date,
      today,
    ].filter(Boolean);
    const uniqueDates = [...new Set(dateCandidates)];

    /** Dedupe: same folder may appear in extraFolders and date-derived paths. */
    const folderPaths = new Set();
    for (const d of extraFolders || []) {
      if (d && typeof d === "string")
        folderPaths.add(d.replace(/^\/+|\/+$/g, ""));
    }
    for (const dateStr of uniqueDates) {
      if (!dateStr || typeof dateStr !== "string") continue;
      const y = dateStr.slice(0, 4);
      folderPaths.add(`${schema}/${y}/${dateStr}`);
    }

    const candidates = [];

    const folderList = [...folderPaths];

    for (const folder of folderList) {
      await fetchTranscriptionJsonCandidatesFromFolder(
        patientId,
        folder,
        candidates,
      );
    }

    if (candidates.length === 0) {
      const probed = await probeDirectTranscriptionUrlsForFolders(
        patientId,
        mergedState,
        folderList,
      );
      if (probed) return probed;
      return null;
    }

    candidates.sort(
      (a, b) =>
        new Date(b.backupTimestamp || 0).getTime() -
        new Date(a.backupTimestamp || 0).getTime(),
    );
    return candidates[0];
  } catch (e) {
    console.error(
      "[TRANSCRIPTION_BACKUP] findTranscriptionBackupInSupabaseStorage:",
      e,
    );
    return null;
  }
}

/**
 * Search for transcription backup by patient name, EHR ID, audio number, and date
 * @param {string} patientName - Patient name
 * @param {string} ehrId - EHR ID
 * @param {number} audioNumber - Audio number
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} schemaName - Schema name (optional)
 */
export const searchTranscriptionBackup = async (
  patientName,
  ehrId,
  audioNumber,
  date,
  schemaName = "public",
) => {
  try {
    const backendUrl = getBackendURL();
    const url = `${backendUrl}/api/transcription-backup/search/?patient_name=${encodeURIComponent(patientName)}&ehr_id=${encodeURIComponent(ehrId)}&audio_number=${encodeURIComponent(audioNumber)}&date=${encodeURIComponent(date)}&schema_name=${encodeURIComponent(schemaName)}`;

    console.log("💾 [TRANSCRIPTION_BACKUP] Searching for backup with:");
    console.log("  patientName:", patientName);
    console.log("  ehrId:", ehrId);
    console.log("  audioNumber:", audioNumber);
    console.log("  date:", date);
    console.log("  schemaName:", schemaName);
    console.log("  Full URL:", url);

    const response = await fetch(url);
    console.log(
      "💾 [TRANSCRIPTION_BACKUP] Search response status:",
      response.status,
    );

    if (response.status === 404) {
      console.log(
        "💾 [TRANSCRIPTION_BACKUP] No backup found at the specific path",
      );
      return null;
    }

    const result = await response.json();
    console.log("💾 [TRANSCRIPTION_BACKUP] Search result:", result);

    if (result.success && result.backup) {
      console.log(
        "💾 [TRANSCRIPTION_BACKUP] Found backup by name/EHR/date:",
        result.backup,
      );
      return result.backup;
    }

    console.log("💾 [TRANSCRIPTION_BACKUP] No backup found");
    return null;
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Search backup error:", error);
    return null;
  }
};

/**
 * Cleanup old backups for a session
 * @param {string} patientId - Patient identifier
 * @param {string} sessionId - Session identifier
 * @param {number} keepCount - Number of backups to keep
 */
export const cleanupSessionBackups = async (
  patientId,
  sessionId,
  keepCount = 10,
) => {
  try {
    const backendUrl = getBackendURL();

    const response = await fetch(
      backendUrl + "/api/transcription-backup/cleanup/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          patient_id: patientId,
          session_id: sessionId,
          keep_count: keepCount,
        }),
      },
    );

    const result = await response.json();
    console.log("💾 [TRANSCRIPTION_BACKUP] Cleanup result:", result);

    return result;
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Cleanup error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Cleanup all expired backups
 * @param {number} retentionHours - Hours to retain backups
 */
export const cleanupExpiredBackups = async (
  retentionHours = BACKUP_CONFIG.RETENTION_HOURS,
) => {
  try {
    const backendUrl = getBackendURL();

    const response = await fetch(
      backendUrl + "/api/transcription-backup/cleanup-expired/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          retention_hours: retentionHours,
        }),
      },
    );

    const result = await response.json();
    console.log("💾 [TRANSCRIPTION_BACKUP] Expired cleanup result:", result);

    return result;
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Expired cleanup error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Recover transcription from the latest backup
 * @param {string} patientId - Patient identifier
 * @param {string} sessionId - Session identifier
 */
export const recoverFromBackup = async (patientId, sessionId) => {
  try {
    console.log("💾 [TRANSCRIPTION_BACKUP] Attempting recovery from backup");

    const backup = await getLatestBackup(patientId, sessionId);

    if (backup) {
      console.log(
        "💾 [TRANSCRIPTION_BACKUP] Recovery successful, restoring transcript",
      );
      return {
        success: true,
        transcriptText: backup.transcript_text || backup.text,
        duration: backup.duration,
        metadata: backup.metadata,
        backupTimestamp: backup.backup_timestamp || backup.created_at,
      };
    }

    console.log("💾 [TRANSCRIPTION_BACKUP] No backup available for recovery");
    return { success: false, error: "No backup available" };
  } catch (error) {
    console.error("💾 [TRANSCRIPTION_BACKUP] Recovery error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get current backup session status
 */
export const getBackupStatus = () => {
  return {
    isActive: backupIntervalId !== null,
    sessionId: currentSessionId,
    backupCount: backupCount,
    lastBackupTime: lastBackupTime,
    intervalMs: BACKUP_CONFIG.INTERVAL_MS,
    pathInfo: currentPathInfo,
  };
};

/**
 * End the current backup session
 */
export const endBackupSession = () => {
  console.log("💾 [TRANSCRIPTION_BACKUP] Ending backup session");

  stopPeriodicBackup();

  const status = {
    sessionId: currentSessionId,
    totalBackups: backupCount,
    lastBackupTime: lastBackupTime,
    pathInfo: currentPathInfo,
  };

  currentSessionId = null;
  backupCount = 0;
  lastBackupTime = null;
  currentPathInfo = null;

  console.log("💾 [TRANSCRIPTION_BACKUP] Session ended:", status);

  return status;
};

/**
 * Save a manual checkpoint backup
 * @param {string} transcriptText - Current transcription text
 * @param {string} patientId - Patient identifier
 * @param {Object} additionalData - Additional data to include in backup
 */
export const saveCheckpointBackup = async (
  transcriptText,
  patientId,
  additionalData = {},
) => {
  if (!currentSessionId) {
    console.warn(
      "💾 [TRANSCRIPTION_BACKUP] Cannot save checkpoint - no active session",
    );
    return { success: false, error: "No active session" };
  }

  // Get path info for folder structure
  const pathInfo = currentPathInfo || {};

  const backupPayload = {
    patient_id: patientId || "unknown",
    session_id: currentSessionId, // Must match parent audio's session_id for proper matching
    transcript_text: transcriptText,
    duration: additionalData.duration || 0,
    timestamp: new Date().toISOString(),
    // New path parameters for folder structure matching audio files
    schema_name: additionalData.schema_name || pathInfo.schemaName || "public",
    year:
      additionalData.year ||
      pathInfo.year ||
      new Date().getFullYear().toString(),
    date:
      additionalData.date ||
      pathInfo.date ||
      new Date().toISOString().split("T")[0],
    patient_name: additionalData.patient_name || pathInfo.patientName || "",
    ehr_id: additionalData.ehr_id || pathInfo.ehrId || "unknown_ehr",
    audio_number: additionalData.audio_number || pathInfo.audioNumber || 1,
    metadata: {
      ...additionalData.metadata,
      checkpoint: true,
      manual: true,
      source: "manual_checkpoint",
      session_id: currentSessionId, // Must match parent audio's session_id
    },
  };

  console.log("💾 [TRANSCRIPTION_BACKUP] Saving manual checkpoint");
  return await saveTranscriptionBackup(backupPayload);
};

/**
 * Persist the transcript carried by a recorder-popup message
 * (AUDIO_RECORDING_BACKUP / RECORDING_PAUSED / RECORDING_ABRUPTLY_CLOSED /
 * RECORDING_STOPPED) without depending on which parent component is mounted.
 * Registers the session on first sight, then queues the save.
 *
 * @param {Object} data - popup postMessage payload
 * @param {Object} [opts]
 * @param {string} [opts.visitDate] - YYYY-MM-DD fallback when the message has no scheduledDate
 * @param {string} [opts.recordingState] - stored in metadata.recording_state
 */
export const saveTranscriptBackupFromRecorderMessage = async (
  data,
  opts = {},
) => {
  const sessionId = data?.sessionId || data?.session_id;
  const patientId = data?.patientId || data?.patient_id;
  const transcriptText = String(
    data?.transcriptText ?? data?.liveTranscriptText ?? data?.finalTranscript ?? "",
  );
  if (!sessionId || !patientId) {
    return { success: false, error: "Missing sessionId or patientId" };
  }
  if (!transcriptText.trim()) {
    return { success: false, error: "No transcription data" };
  }

  const scheduledDate = data.scheduledDate || opts.visitDate || null;
  let schemaName = data.schemaName || data.schema_name || null;
  if (!schemaName) {
    try {
      schemaName = (await getUserSchema()) || "public";
    } catch {
      schemaName = "public";
    }
  }

  ensureBackupSession(sessionId, patientId, {
    schemaName,
    year: scheduledDate ? String(scheduledDate).slice(0, 4) : undefined,
    date: scheduledDate || undefined,
    patientName: data.patientName || data.patient_name || "",
    ehrId: data.ehrId || data.ehr_id || undefined,
    audioNumber: data.audioNumber ?? data.audio_number ?? undefined,
  });

  return saveTranscriptionBackup({
    sessionId,
    patientId,
    transcriptText,
    duration: data.duration || 0,
    timestamp: data.timestamp || new Date().toISOString(),
    capturedAt: data.capturedAt || data.timestamp || Date.now(),
    metadata: {
      recording_state: opts.recordingState || "recording",
      source: opts.source || "recorder_message",
      session_id: sessionId,
    },
  });
};

/**
 * Configure backup settings
 * @param {Object} config - Configuration overrides
 */
export const configureBackup = (config) => {
  Object.assign(BACKUP_CONFIG, config);
  console.log(
    "💾 [TRANSCRIPTION_BACKUP] Configuration updated:",
    BACKUP_CONFIG,
  );
};

// Export configuration for external access
export { BACKUP_CONFIG };
